import { Head, Link } from "@inertiajs/react";

import DashboardAnnouncements from "@/components/announcements/dashboard-announcements";
import { DashboardCharts } from "@/components/dashboard-charts";
import { PlaceholderPattern } from "@/components/placeholder-pattern";
import { Button } from "@/components/ui/button";
import AppLayout from "@/layouts/app-layout";
import {
  dashboardPath,
  newPurchaseRequestPath,
  productsPath,
  purchaseRequestsPath,
} from "@/routes";
import type { AnnouncementData, BreadcrumbItem, DashboardChartsData } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: dashboardPath(),
  },
];

interface DashboardProps {
  announcements: AnnouncementData;
  chartsData?: DashboardChartsData;
}

export default function Dashboard({ announcements, chartsData }: DashboardProps) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Dashboard" />
      <div className="flex h-full flex-1 flex-col gap-4 overflow-x-auto rounded-xl p-4">
        {chartsData && (
          <div className="mb-4">
            <DashboardCharts data={chartsData} />
          </div>
        )}
        <div className="grid auto-rows-min gap-4 md:grid-cols-3">
          <div className="border-sidebar-border/70 dark:border-sidebar-border rounded-xl border p-6 h-full flex flex-col">
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-semibold">Solicitudes de Compra</h3>
              <p className="text-muted-foreground">
                Gestiona y carga solicitudes de compra.
              </p>
            </div>
            <div className="mt-auto flex flex-wrap gap-2">
              <Button asChild>
                <Link href={purchaseRequestsPath()}>Ver listado</Link>
              </Button>
              <Button asChild variant="secondary">
                <Link href={newPurchaseRequestPath()}>
                  Crear solicitud
                </Link>
              </Button>
            </div>
          </div>

          <div className="border-sidebar-border/70 dark:border-sidebar-border rounded-xl border p-6 h-full flex flex-col">
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-semibold">Productos</h3>
              <p className="text-muted-foreground">
                Consulta y administra el catálogo de productos.
              </p>
            </div>
            <div className="mt-auto flex flex-wrap gap-2">
              <Button asChild>
                <Link href={productsPath()}>Ver productos</Link>
              </Button>
            </div>
          </div>

          <div className="border-sidebar-border/70 dark:border-sidebar-border rounded-xl border p-6 h-full flex flex-col">
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-semibold">Ayuda e Información</h3>
              <p className="text-muted-foreground">
                ¿Tenés dudas o sugerencias? Accedé al formulario o al
                tutorial.
              </p>
            </div>
            <div className="mt-auto pt-4 flex flex-wrap gap-2">
              <Button asChild>
                <a
                  href="https://tally.so/r/nre9Ll"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Enviar sugerencia (Tally)
                </a>
              </Button>
              <Button asChild variant="secondary">
                <a href="/user-manual" target="_blank" rel="noopener noreferrer">
                  Ver manual
                </a>
              </Button>
            </div>
          </div>
        </div>
        <div className="border-sidebar-border/70 dark:border-sidebar-border relative min-h-[100vh] flex-1 overflow-hidden rounded-xl border md:min-h-min">
          {announcements && announcements.announcements?.length > 0 ? (
            <div className="relative h-full">
              <DashboardAnnouncements data={announcements} />
            </div>
          ) : (
            <PlaceholderPattern className="absolute inset-0 size-full stroke-neutral-900/20 dark:stroke-neutral-100/20" />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
